import React from "react";
import {
  VStack,
  Flex,
  Icon,
  Text,
  Box,
  IconButton,
  Spinner,
  Center,
  Badge,
} from "@chakra-ui/react";
import { PDFIcon } from "@root/assets/icons/PDFIcon";
import DocxIcon from "@root/assets/icons/doxcicon";
import { FILE_EXTENION, FILE_STATUS_NUMERIC } from "@root/constants";
import { XlsxIcon } from "@root/assets/icons/Xlsx";
import { DeleteIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@root/assets/icons/refreshicon";

interface FileListViewProps {
  files: any[];
  onDeleteClick?: (file: any) => void;
  onRetryClick?: (file: any) => void;
  fileLoading: boolean;
}

/**
 * Gets status configuration for compact status badge
 * @param status - File status number (1 = IN_PROGRESS, 2 = COMPLETED, 3 = FAILED)
 * @param t - Translation function
 * @returns Status config with colors and text
 */
const getStatusConfig = (
  status: number | undefined,
  t: (key: string) => string
) => {
  if (status === FILE_STATUS_NUMERIC.IN_PROGRESS) {
    return {
      text: t("upload_document.processing"),
      bgColor: "#2442A914",
      textColor: "#2442A9",
      borderColor: "#2442A9",
    };
  }
  if (status === FILE_STATUS_NUMERIC.COMPLETED) {
    return {
      text: t("upload_document.completed"),
      bgColor: "#0FABBE",
      textColor: "#FFFFFF",
      borderColor: "#0FABBE",
    };
  }
  if (status === FILE_STATUS_NUMERIC.FAILED) {
    return {
      text: t("upload_document.failed"),
      bgColor: "#FF000014",
      textColor: "#FF0000",
      borderColor: "#FF0000",
    };
  }
  // Default to processing
  return {
    text: t("upload_document.processing"),
    bgColor: "#2442A914",
    textColor: "#2442A9",
    borderColor: "#2442A9",
  };
};

const FileListView: React.FC<FileListViewProps> = ({
  files,
  onDeleteClick,
  onRetryClick,
  fileLoading,
}) => {
  const { t } = useTranslation("common");
  return (
    <VStack
      align="stretch"
      spacing={2}
      h="500px" // Or whatever height your container should have
      justifyContent={fileLoading ? "center" : "flex-start"} // Centers spinner
    >
      {fileLoading ? (
        <Center w="full">
          <Spinner size="sm" thickness="2px" />
        </Center>
      ) : !files || files?.length === 0 ? (
        <Center w="full" p={3}>
          <Text fontSize="xs">{t("projects.noFilesAvailable")}</Text>
        </Center>
      ) : (
        files?.map((file) => {
          const statusConfig =
            file?.status !== undefined
              ? getStatusConfig(file?.status, t)
              : null;

          return (
            <Flex
              key={file?.id}
              align="center"
              p={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.100"
              _hover={{ bg: "gray.50" }}
              cursor="pointer"
              role="group"
            >
              {/* Icon wrapper */}
              <Box
                boxSize={5}
                mr={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {file?.extension === FILE_EXTENION.PDF && (
                  <Icon as={PDFIcon} boxSize={4} color="teal.500" />
                )}
                {file?.extension === FILE_EXTENION.DOCX && (
                  <Icon as={DocxIcon} boxSize={4} color="teal.500" />
                )}
                {file?.extension === FILE_EXTENION.XLSX && (
                  <Icon as={XlsxIcon} boxSize={4} color="teal.500" />
                )}
              </Box>

              {/* File name */}
              <Box flex="1" minW={0}>
                <Text fontSize="sm" isTruncated>
                  {file?.fileName_key}
                </Text>
              </Box>

              {/* Delete button */}
              <IconButton
                aria-label={t("projects.deleteFile")}
                icon={<DeleteIcon color="black" />}
                size="xs"
                variant="ghost"
                opacity={0}
                _groupHover={{ opacity: 1 }}
                transition="opacity 0.2s"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick?.(file);
                }}
              />

              {/* Compact status badge */}
              {statusConfig && (
                <Flex flexShrink={0} ml={1.5} align="center" gap={1}>
                  <Badge
                    fontSize="10px"
                    fontWeight={500}
                    px={1}
                    py={0.5}
                    borderRadius="3px"
                    border="1px solid"
                    borderColor={statusConfig.borderColor}
                    bg={statusConfig.bgColor}
                    color={statusConfig.textColor}
                    textTransform="none"
                    lineHeight="1.1"
                  >
                    {statusConfig.text}
                  </Badge>
                  {/* Retry button for failed status */}
                  {file?.status === FILE_STATUS_NUMERIC.FAILED &&
                    onRetryClick && (
                      <IconButton
                        aria-label={t("retry")}
                        icon={<RefreshIcon width={14} height={14} />}
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRetryClick(file);
                        }}
                        _hover={{ bg: "gray.100" }}
                      />
                    )}
                </Flex>
              )}
            </Flex>
          );
        })
      )}
    </VStack>
  );
};

export default FileListView;
